import React, { useEffect, useState, useMemo } from 'react';
import dayjs from 'dayjs';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Typography,
  Tag,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { taskTypeService, TaskType, CreateTaskTypeData } from '../services/taskTypes';
import { useTableColumnSearch } from '../hooks/useTableColumnSearch';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { ColumnVisibilityToggle } from '../components/ColumnVisibilityToggle';
import '../styles/resizable-table.css';

const { Title } = Typography;

export const TaskTypes: React.FC = () => {
  const listingKey = 'task_types';
  const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTaskType, setEditingTaskType] = useState<TaskType | null>(null);
  const [form] = Form.useForm<CreateTaskTypeData>();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await taskTypeService.getAll();
      setTaskTypes(response.data || []);
    } catch {
      message.error('Failed to load task types data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTaskType(null);
    form.resetFields();
    form.setFieldsValue({ enabled: true });
    setModalVisible(true);
  };

  const handleEdit = (taskType: TaskType) => {
    setEditingTaskType(taskType);
    form.setFieldsValue({
      ...taskType,
      enabled: taskType.enabled,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await taskTypeService.delete(id);
      message.success('Task type deleted');
      fetchData();
    } catch {
      message.error('Failed to delete task type');
    }
  };

  const handleSubmit = async (values: CreateTaskTypeData) => {
    const payload = {
      ...values,
      enabled: values.enabled ?? true,
    };

    try {
      if (editingTaskType) {
        await taskTypeService.update(editingTaskType.id, payload);
        message.success('Task type updated');
      } else {
        await taskTypeService.create(payload);
        message.success('Task type created');
      }
      setModalVisible(false);
      fetchData();
    } catch {
      message.error(editingTaskType ? 'Failed to update task type' : 'Failed to create task type');
    }
  };

  const baseColumns = [
    { 
      title: 'Name', 
      dataIndex: 'name', 
      key: 'name', 
      width: 200,
      sorter: (a: TaskType, b: TaskType) => (a.name || '').localeCompare(b.name || ''),
    },
    {
      title: 'Enabled',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 120,
      sorter: (a: TaskType, b: TaskType) => (a.enabled ? 1 : 0) - (b.enabled ? 1 : 0),
      render: (enabled: boolean) => (
        <Tag color={enabled ? 'green' : 'red'}>{enabled ? 'Yes' : 'No'}</Tag>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      sorter: (a: TaskType, b: TaskType) => {
        if (!a.created_at && !b.created_at) return 0;
        if (!a.created_at) return 1;
        if (!b.created_at) return -1;
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      },
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Updated At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 150,
      sorter: (a: TaskType, b: TaskType) => {
        if (!a.updated_at && !b.updated_at) return 0;
        if (!a.updated_at) return 1;
        if (!b.updated_at) return -1;
        return new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime();
      },
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
    },
  ];

  const {
    columnConfig,
    visibleColumns,
    visibleKeys,
    handleColumnVisibilityChange,
  } = useColumnVisibility(baseColumns);

  const memoizedColumns = useMemo(
    () =>
      visibleColumns.map((col) => ({
        ...col,
        width: col.width,
        getSearchValue:
          col.dataIndex === 'name'
            ? (record: TaskType) => record.name
            : col.dataIndex === 'enabled'
            ? (record: TaskType) => (record.enabled ? 'Yes' : 'No')
            : col.dataIndex === 'created_at'
            ? (record: TaskType) => record.created_at
            : col.dataIndex === 'updated_at'
            ? (record: TaskType) => record.updated_at
            : (undefined as unknown as (record: TaskType) => string | number | null | undefined),
      })),
    [visibleColumns]
  );

  const { columnsWithSearch, filteredData } = useTableColumnSearch<TaskType>(
    taskTypes,
    memoizedColumns as any,
    memoizedColumns as any,
    { listingKey }
  );
  const tableColumns = useMemo(() => {
    const widths = columnsWithSearch.map((col) => {
      const rawWidth = col.width;
      if (typeof rawWidth === 'number') {
        return rawWidth;
      }
      if (typeof rawWidth === 'string') {
        const parsed = Number.parseFloat(rawWidth);
        return Number.isFinite(parsed) ? parsed : 150;
      }
      return 150;
    });
    const total = widths.reduce((sum, width) => sum + width, 0) || 1;
    return columnsWithSearch.map((col, index) => ({
      ...col,
      width: `${(widths[index] / total) * 100}%`,
    }));
  }, [columnsWithSearch]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={3} style={{ margin: 0 }}>
            Task Types
          </Title>
          <Space>
            <ColumnVisibilityToggle
              columns={columnConfig}
              visibleKeys={visibleKeys}
              onChange={handleColumnVisibilityChange}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              New Task Type
            </Button>
          </Space>
        </div>

        <Table
          columns={tableColumns as any}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          pagination={false}
          footer={() => (
            <div style={{ textAlign: 'left', padding: '8px 0' }}>
              Total: {filteredData.length} {filteredData.length === 1 ? 'task type' : 'task types'}
            </div>
          )}
          onRow={(record) => ({
            onClick: () => handleEdit(record),
            style: { cursor: 'pointer' },
          })}
        />

      <Modal
        title={editingTaskType ? 'Edit Task Type' : 'New Task Type'}
        open={modalVisible}
        footer={null}
        onCancel={() => setModalVisible(false)}
        width={520}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Task Type Name"
            rules={[{ required: true, message: 'Please enter task type name' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="enabled" label="Enabled" valuePropName="checked" initialValue>
            <Switch />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button onClick={() => setModalVisible(false)}>Cancel</Button>
              {editingTaskType && (
                <Button 
                  danger 
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this task type?')) {
                      handleDelete(editingTaskType.id);
                      setModalVisible(false);
                    }
                  }}
                >
                  Delete
                </Button>
              )}
              <Button type="primary" htmlType="submit">
                {editingTaskType ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
